import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Sparkles, ExternalLink, Camera, Video, Calendar, Share2, Globe2, Mail, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// YouTube video-id extractor
function ytId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
    if (u.hostname.endsWith("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
    return null;
  } catch { return null; }
}

function prettyRange(start, end) {
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parse = (s) => { if (!s || s.length < 7) return null; const [y, m] = s.split("-").map(Number); return { y, m: m - 1 }; };
  const a = parse(start), b = parse(end);
  if (!a && !b) return "";
  if (a && !b) return `${M[a.m]} ${a.y}`;
  if (!a && b) return `${M[b.m]} ${b.y}`;
  if (a.y === b.y && a.m === b.m) return `${M[a.m]} ${a.y}`;
  if (a.y === b.y) return `${M[a.m]}–${M[b.m]} ${a.y}`;
  return `${M[a.m]} ${a.y} – ${M[b.m]} ${b.y}`;
}

function shareText(row) {
  const when = prettyRange(row.start_date, row.end_date);
  const prog = row.programme || "India Experience Program";
  return [
    `Meet ${row.customer} — a participant on our ${prog}${when ? ` in ${when}` : ""}. 🌏`,
    ``,
    `📸 Photos: ${row.pictures_link}`,
    `🎥 Testimonial: ${row.testimonials_link}`,
    ``,
    `— Aii Group · Meaningful Adventures`,
  ].join("\n");
}
async function shareStory(row) {
  const text = shareText(row);
  const title = `Aii Group — ${row.customer}'s story`;
  if (navigator.share) {
    try { await navigator.share({ title, text, url: row.testimonials_link }); return; }
    catch (e) { if (e && e.name === "AbortError") return; }
  }
  try { await navigator.clipboard.writeText(text); toast.success("Story copied — paste into WhatsApp or LinkedIn"); }
  catch { toast.error("Could not copy. Please try again."); }
}

function StoryCard({ row }) {
  const vid = ytId(row.testimonials_link);
  const thumb = vid ? `https://img.youtube.com/vi/${vid}/hqdefault.jpg` : null;

  return (
    <article className="rounded-2xl bg-white border border-[color:var(--aii-border)] overflow-hidden flex flex-col shadow-sm hover:shadow-lg transition-shadow duration-300 group">
      <a href={row.testimonials_link} target="_blank" rel="noreferrer"
        className="relative aspect-video bg-[color:var(--aii-green-tint)] overflow-hidden block">
        {thumb ? (
          <img src={thumb} alt={`${row.customer} testimonial`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Video size={48} className="text-brand/60" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent pointer-events-none" />
        <span className="absolute bottom-3 left-3 text-white text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5">
          <Video size={12} /> Watch testimonial
        </span>
      </a>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-lg font-semibold leading-tight">{row.customer}</h3>
        <div className="text-sm text-[color:var(--aii-text-2)] mt-0.5">{row.programme || "India Experience Program"}</div>
        {(row.start_date || row.end_date) && (
          <div className="text-xs text-[color:var(--aii-muted)] mt-2 flex items-center gap-1.5">
            <Calendar size={11} />
            {prettyRange(row.start_date, row.end_date)}
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-[color:var(--aii-border)] flex flex-wrap gap-2">
          <a href={row.pictures_link} target="_blank" rel="noreferrer"
            className="flex-1 min-w-[110px] text-center text-xs font-semibold px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark flex items-center justify-center gap-1.5">
            <Camera size={12} /> View pictures
          </a>
          <a href={row.testimonials_link} target="_blank" rel="noreferrer"
            className="flex-1 min-w-[110px] text-center text-xs font-semibold px-3 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center gap-1.5">
            <ExternalLink size={12} /> Watch video
          </a>
          <button onClick={() => shareStory(row)} title="Share this story"
            className="text-xs font-semibold px-3 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center justify-center gap-1.5">
            <Share2 size={12} /> Share
          </button>
        </div>
      </div>
    </article>
  );
}

export default function PublicStories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/stories`).then(({ data }) => { setRows(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const stories = useMemo(() => rows, [rows]);

  return (
    <div className="min-h-screen bg-[color:var(--aii-bg)]">
      {/* Header */}
      <header className="bg-white border-b border-[color:var(--aii-border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/aii-logo.png" alt="Aii Group" className="w-10 h-10 sm:w-12 sm:h-12 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-base sm:text-lg font-bold leading-none">AII GROUP<span className="text-brand">®</span></div>
              <div className="text-[10px] sm:text-xs text-[color:var(--aii-muted)] mt-0.5">Meaningful Adventures</div>
            </div>
          </div>
          <a href="/login" className="text-xs sm:text-sm font-semibold text-brand hover:text-brand-dark hidden sm:flex items-center gap-1">
            Team login <ArrowUpRight size={14} />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand font-semibold mb-3">
            <Sparkles size={14} /> Real journeys · Real people
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-semibold leading-tight tracking-tight">
            Stories from our <span className="text-brand">volunteers &amp; interns</span> in&nbsp;India.
          </h1>
          <p className="text-base sm:text-lg text-[color:var(--aii-text-2)] mt-4 max-w-2xl">
            Every card below is a real participant who lived, learned and made an impact with us. Watch their video,
            browse their photos, and imagine what your journey could look like.
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="text-center py-16 text-[color:var(--aii-muted)]">Loading stories…</div>
        ) : stories.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="mx-auto mb-3 text-brand" size={32} />
            <div className="font-display text-xl font-semibold">Stories coming soon</div>
            <p className="text-sm text-[color:var(--aii-text-2)] mt-2 max-w-md mx-auto">
              We&apos;re gathering photos and testimonials from our latest cohort. Check back shortly.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-sm text-[color:var(--aii-text-2)]">
              <b className="text-brand">{stories.length}</b> {stories.length === 1 ? "story" : "stories"} to explore.
            </div>
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {stories.map((r) => <StoryCard key={r.id} row={r} />)}
            </div>
          </>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[color:var(--aii-border)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <img src="/aii-logo.png" alt="Aii Group" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-display font-semibold">Aii Group<span className="text-brand">®</span></div>
              <div className="text-xs text-[color:var(--aii-muted)]">Jaipur, India · GSTIN 08AOBPG8423N1ZP</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[color:var(--aii-text-2)]">
            <a href="https://theaiigroupteam.com" target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-brand"><Globe2 size={14}/> Website</a>
            <a href="mailto:hello@aiigroup.com" className="flex items-center gap-1 hover:text-brand"><Mail size={14}/> Contact</a>
            <a href="/login" className="flex items-center gap-1 hover:text-brand sm:hidden">Team login <ArrowUpRight size={14}/></a>
          </div>
        </div>
      </footer>
    </div>
  );
}
